import Encoding from 'encoding-japanese'
import { parse } from 'papaparse'
import ExcelJS from 'exceljs'
import * as XLSX from 'xlsx'
import localforage from 'localforage'
import React from 'react'
import { produce } from 'immer'
import { getFileDataKey } from './services'
import { formatExcelDateCellDisplay } from './excelCellFormat'

/**
 * @template {readonly string[]} T
 * @param {T} keys
 * @returns {Readonly<Record<T[number], T[number]>>}
 */
const generateEnum = (keys) => Object.freeze(Object.fromEntries(keys.map((e) => [e, e])))

/** マッピングタイプラベル */
export const FileMappingTypeLabel = Object.freeze({
  'no-change': '変換なし',
  date: '日付',
  map: '値変換'
})

/** マッピングタイプEnum */
export const FileMappingTypeEnum = Object.freeze({
  'no-change': 'no-change',
  date: 'date',
  map: 'map'
})

/** ファイル状態 */
export const FileStatusLabel = Object.freeze({
  new: '新規',
  checking: 'チェック中',
  'check-ok': 'チェック済み',
  'pending-deletion': '削除待ち',
  error: 'エラー',
  'validation-error': 'エラー'
})

/** ファイル状態Enum */
export const FileStatusEnum = generateEnum([
  'new',
  'checking',
  'check-ok',
  'pending-deletion',
  'validation-error',
  'error'
])

// IndexDbに保存用
/** @type {import('localforage')} */
export const localForage = localforage.createInstance({
  name: 'generic_upload_files',
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE]
})

/**
 * @function createBufferedIo チャンクを使いブラウザに保存
 * @param {LocalForage} localForage
 * @param {string} key // {親タスクキー}/{ファイル名}
 * @param {number} chunkSize
 */
export function createBufferedIOForCsv(localForage, key, chunkSize = 100) {
  let buffer = []
  const flush = async () => {
    if (buffer.length === 0) return

    const sheetName = 0

    // 既存のアイテム
    const existing = (await localForage.getItem(key)) ?? {}

    const changed = produce(existing, (draft) => {
      if (!draft[sheetName]) {
        draft[sheetName] = buffer.flat()
      } else {
        draft[sheetName].push(...buffer.flat())
      }
    })

    await localForage.setItem(key, changed)
    buffer = []
  }

  const add = async (row) => {
    buffer.push(row)

    if (buffer.length >= chunkSize) {
      await flush()
    }
  }

  const finish = async () => {
    await flush()
  }
  return { add, finish }
}

/**
 * @function useRDispatch
 * @param {React.Dispatch<any>} dispatch
 * @returns {(type: string, payload: any) => void}
 */
export const useRDispatch = (dispatch) => (type, payload) => dispatch({ type, payload })

/**
 * @function convertToSHA256 文字列をハッシュ化する関数
 * @param {string} message
 */
export const convertToSHA256 = async (message) => {
  const encoder = new TextEncoder(message)
  const data = encoder.encode(message)
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Byteを上位の単位に変更 */
export function formatBytes(bytes, decimals = 2) {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k)) // log 1024 (1024*1024 bytes) = 2
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}` //  1024*1024 / 1024^2 = 1 MB
}

/**
 * @function getEncoding ファイルのエンコードを取得
 * @param {File} file
 * @param {function(number):void} onProgress
 */
const getEncoding = async (file, onProgress) => {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onprogress = (e) => {
      const progress = Math.round((e.loaded / e.total) * 100)
      onProgress?.(progress)
    }
    reader.onloadend = () => {
      onProgress?.(100)
    }

    reader.onload = (e) => {
      const codes = new Uint8Array(e.target.result)
      const encoding = Encoding.detect(codes)
      resolve(encoding)
    }

    reader.readAsArrayBuffer(file)
  })
}

/**
 * @function readCSVFileStream CSVファイルを読み込み
 * @param {File} file
 * @param {number} chunkSize
 * @param {function(number):void} onProgress
 * @yields {string[]}
 * @generator
 */
export async function* readCsvFileStream(file, chunkSize = 20, onProgress) {
  /** @type {string[][]} */
  const queue = []
  /** @type {string[]} */
  let rows = []
  let done = false
  /** @type {function | null} */
  let lateResolve = null

  const resolver = () => {
    if (lateResolve) {
      lateResolve()
      lateResolve = null
    }
  }

  const encoding = await getEncoding(file, onProgress)

  parse(file, {
    worker: true,
    encoding,
    skipEmptyLines: true,
    step: (e) => {
      const row = e.data
      rows.push(row)

      if (rows.length === chunkSize) {
        queue.push(rows)
        rows = []
        resolver()
      }
    },
    complete: () => {
      if (rows.length > 0) {
        queue.push(rows)
        rows = []
      }
      done = true
      resolver()
    },
    error: () => {
      done = true
      resolver()
    }
  })

  while (!done || queue.length > 0) {
    if (queue.length > 0) {
      yield queue.shift()
    } else {
      await new Promise((resolve) => (lateResolve = resolve))
    }
  }
}

/**
 * レガシーExcel形式（.xls）かどうか判定する（.xlsx/.xlsm等は除外）
 *
 * @param {File} file
 * @returns {boolean}
 */
const isLegacyXlsFile = (file) => {
  const name = (file.name || '').toLowerCase()
  return name.endsWith('.xls') && !name.endsWith('.xlsx') && !name.endsWith('.xlsm') && !name.endsWith('.xlsb')
}

/**
 * レガシー.xlsをExcelJS.Workbookに変換する
 *
 * @param {Uint8Array} codes
 * @returns {Promise<ExcelJS.Workbook>}
 */
const loadLegacyXlsToWorkbook = async (codes) => {
  const xlsWorkbook = XLSX.read(codes, { type: 'array', cellDates: true })
  const xlsxArray = XLSX.write(xlsWorkbook, { bookType: 'xlsx', type: 'array', cellDates: true })
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(xlsxArray)
  return workbook
}

/**
 * .xlsx形式をExcelJS.Workbookに読み込む
 *
 * @param {Uint8Array} codes
 * @returns {Promise<ExcelJS.Workbook>}
 */
const loadXlsxToWorkbook = async (codes) => {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(codes)
  return workbook
}

/**
 * @function readExcelFile
 * @param {File} file
 * @param {function(number):void} onProgress
 * @returns {Promise<ExcelJS.Workbook>}
 */
export async function readExcelFile(file, onProgress) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()

    fileReader.onload = async (e) => {
      const codes = new Uint8Array(e.target.result)

      try {
        const workbook = isLegacyXlsFile(file) ? await loadLegacyXlsToWorkbook(codes) : await loadXlsxToWorkbook(codes)
        resolve(workbook)
      } catch (error) {
        reject(error)
      }
    }

    fileReader.onprogress = (e) => {
      const progress = Math.round((e.loaded / e.total) * 100)
      onProgress?.(progress)
    }
    fileReader.onloadend = () => {
      onProgress?.(100)
    }

    fileReader.readAsArrayBuffer(file)
  })
}

/**
 * @function readWorkSheet
 * @param {File} file
 * @param {function(number):void} onProgress
 */
export async function readWorkSheet(file, onProgress) {
  const workbook = await readExcelFile(file, onProgress)

  /** @type {{ sheetId: string; sheetName: string; worksheet: ExcelJS.WorkSheet }[]} */
  const sheetList = []

  workbook.eachSheet((ws, id) => {
    sheetList.push({
      sheetId: id,
      sheetName: ws.name,
      worksheet: workbook.getWorksheet(id)
    })
  })

  return sheetList
}

/**
 * @function readWorkSheet エクセルワークシート読み取り
 * @param {import('exceljs').Worksheet} sheet
 * @param {number} rowNum
 */
export function readSheet(sheet, rowNum = 1) {
  const table = {
    headers: [],
    records: []
  }
  // ヘッダー行
  const headerRow = sheet.getRow(rowNum)
  for (let i = 1; true; i++) {
    const headerCell = headerRow.getCell(i)
    let val
    if (headerCell.type === ExcelJS.ValueType.Formula) {
      val = headerCell.result // 数式の計算結果を取得
    } else {
      val = headerCell.value // セル値を取得
    }
    if (!val) {
      break
    }
    table.headers.push(val)
  }
  // レコード行
  for (let i = rowNum + 1; true; i++) {
    const row = sheet.getRow(i)
    if (!row.hasValues) break

    const rec = []
    for (let j = 1; j <= table.headers.length; j++) {
      const cell = row.getCell(j)
      if (cell.type === ExcelJS.ValueType.Formula) {
        rec.push(cell.result) // 数式の計算結果を取得
      } else if (cell.type === ExcelJS.ValueType.Date) {
        rec.push(formatExcelDateCellDisplay(cell.value, cell.numFmt))
      } else {
        if (isRichValue(cell.value)) {
          rec.push(richToString(cell.value))
        } else {
          rec.push(cell.value) // セル値を取得
        }
      }
    }
    table.records.push(rec)
  }

  return table
}

function isRichValue(value) {
  return Boolean(value && Array.isArray(value.richText))
}

function richToString(rich) {
  return rich.richText.map(({ text }) => text).join('')
}

/**
 * @function getFileDataFromIndexDb ファイルデータを読み込み
 * @param {string} taskKey
 * @param {string} fileName
 * @param {string} sheet
 */
export const getFileDataFromIndexDb = async (taskKey, fileName, sheet = 0) => {
  const fileKey = getFileDataKey(taskKey, fileName)

  const item = await localForage.getItem(fileKey)

  if (!item) {
    console.log('存在しない')
    return []
  }

  return item[sheet]
}

/**
 * @function extractUniqueValuesFromColumn
 * @param {object} fileMeta
 * @param {string} fileMeta.taskKey
 * @param {string} fileMeta.fileKey
 * @param {string} fileMeta.sheetName
 */
export const extractUniqueValuesFromColumn = async (fileMeta, columnIndex) => {
  const { taskKey, fileKey, sheetName } = fileMeta
  const records = await getFileDataFromIndexDb(taskKey, fileKey, sheetName || 0)
  return new Set(records.map((e) => convertValuesToString(e[columnIndex])))
}

export const convertValuesToString = (e) => {
  let value = e
  if (value === null || value === undefined) value = ''
  return String(value)
}
